import { SubstepType } from "~/features/configurator/Linear/dtos";
import { getHighestVisitedStep, getMinStepToSave } from "~/features/configurator/Linear/redux/selectors";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { sendLinearUpdate } from "~/features/configurator/Linear/api/sendLinearConfiguratorUpdate";
import { sendLinearDataReset } from "~/features/configurator/Linear/api/sendLinearStepReset";
import { sendLinearStepChange } from "~/features/configurator/Linear/api/sendLinearStepChange";
import { sendLinearResetConfigurator } from "~/features/configurator/Linear/api/sendLinearResetConfigurator";
import { sendLinearAddToCart } from "~/features/configurator/Linear/api/sendLinearAddToCart";
import { LinearSummaryData } from "~/features/configurator/Linear/v2/configuration/classic/summary/LinearSummaryData";
import { type RootState } from "~/app/store";
import {
  BASE_DESIGNATION_SEPARATOR,
  CSTIC_DESIGN_TYPE_NAME,
  CSTIC_SIZE_NAME,
  FAKE_CSTIC_CONFIGURATION_VERSION,
  FAKE_CSTIC_UNIT_LENGTH,
  LINEAR_MIN_STEP_TO_BUY,
} from "~/features/configurator/Linear/constants";
import { LinearSummaryParams } from "~/features/configurator/Linear/components/LinearSummaryParams";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import { isLengthValid } from "~/features/configurator/Linear/redux/reducer";
import { RAIL_CONFIGURATOR_PRODUCT_CODE, UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import * as RailsCstics from "~/features/configurator/Rail/v2/configuration/classic/RailsCstics";
import * as CarriageCstics from "~/features/configurator/Carriage/v2/configuration/CarriageCstics";
import * as UnitCstics from "~/features/configurator/Linear/v2/configuration/classic/UnitCstics";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep3SummaryData,
  getStep4SummaryData,
  getStep5SummaryData,
  getStep6SummaryDataRail,
  getStep7SummaryData,
  getStep7SummaryDataCarriage,
} from "./summary/UnitSummaryDataProvider";
import {
  type CarriageKmatSummaryData,
  prepareSubStepsInfo,
  type RailKmatSummaryData,
  transformCarriageSummaryDataToV2,
  transformRailSummaryDataToV2,
  transformSummaryDataToV2,
  transformToSelectionData,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import { CsticNamingHelper } from "../kllt/CsticNamingHelper";
import instance from "~/utils/i18n";

const UNIT_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.SystemLayout];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step3Data = getStep3SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(3),
        gaName: summaryDataProvider.getGaStepName(3),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step3Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[2].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step4Data = getStep4SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(4),
        gaName: summaryDataProvider.getGaStepName(4),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step4Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[3].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step5Data = getStep5SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(5),
        gaName: summaryDataProvider.getGaStepName(5),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step5Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[2].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const guidewayData: RailKmatSummaryData[] = getStep6SummaryDataRail(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(6),
        gaName: summaryDataProvider.getGaStepName(6),
        tables: [transformRailSummaryDataToV2(guidewayData)],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[5].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state: RootState, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step7Data = getStep7SummaryData(summaryDataProvider) ?? [];
      const carriageData: CarriageKmatSummaryData[] = getStep7SummaryDataCarriage(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(6),
        gaName: summaryDataProvider.getGaStepName(6),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step7Data.map(transformSummaryDataToV2),
          },
          transformCarriageSummaryDataToV2(carriageData),
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = UNIT_CONFIGURATOR_STEP_DATA[6].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const LC_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.System,
  productCode: UNIT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.unit.name",
  },
  apiCalls: {
    update: sendLinearUpdate,
    reset: sendLinearResetConfigurator,
    stepChange: sendLinearStepChange,
    stepReset: sendLinearDataReset,
    addToCart: sendLinearAddToCart,
  },
  summary: LinearSummaryData,
  summaryParams: LinearSummaryParams,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= LINEAR_MIN_STEP_TO_BUY;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= getMinStepToSave(state);
  },
  stepHardcodedData: UNIT_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    if (getConfiguratorDefinition(RAIL_CONFIGURATOR_PRODUCT_CODE).isConflictAvoidable) {
      return true;
    }
    const avoidableCstics = [FAKE_CSTIC_UNIT_LENGTH];

    return avoidableCstics.includes(csticName);
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    let designType = kmat.cstics[CSTIC_DESIGN_TYPE_NAME]?.value;
    const size = kmat.cstics[CSTIC_SIZE_NAME]?.value ?? "";
    let suffix = kmat?.cstics[FAKE_CSTIC_CONFIGURATION_VERSION]?.value;

    if (!designType) {
      return BASE_DESIGNATION_SEPARATOR;
    }

    if (designType?.includes("..-")) {
      [designType, suffix] = designType.split("..-");
    }

    return [designType + size, suffix]
      .filter(Boolean)
      .filter((item) => item !== "/")
      .join("-");
  },
  gaConfiguratorName: "PSF",
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const namingHelper = new CsticNamingHelper(kmat.cstics);
    const cstics = kmat.cstics;

    // Step 3 Length validation
    if (model.substeps[SubstepType.Length]) {
      const mainKmat = getMainKmat({ kmats: model.kmats });
      const lengthCstic = mainKmat?.cstics[FAKE_CSTIC_UNIT_LENGTH];
      const length = lengthCstic?.value ?? null;
      const valid = length && isLengthValid(model.minLength, model.maxLength, Number.parseFloat(length));
      if (length && !valid && "conflictedCstics" in model) {
        model.conflictedCstics.push({
          id: "Invalid length",
          name: "Invalid length",
          cstics: [
            {
              name: FAKE_CSTIC_UNIT_LENGTH,
              key: FAKE_CSTIC_UNIT_LENGTH,
            },
          ],
        });
      }
    }

    namingHelper.updateNames(UnitCstics.RailLocatingFaceSegment1, {
      label: "configurator.unit.cstic.railLocatingFace1.name",
      value: {
        label: (value) => `configurator.unit.cstic.railLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstics.RailLocatingFaceSegment2, {
      label: "configurator.unit.cstic.railLocatingFace2.name",
      value: {
        label: (value) => `configurator.unit.cstic.railLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstics.CarriageLocatingFaceSegment1, {
      label: "configurator.unit.cstic.carriageLocatingFace1.name",
      value: {
        label: (value) => `configurator.unit.cstic.carriageLocatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(UnitCstics.CarriageLocatingFaceSegment2, {
      label: "configurator.unit.cstic.carriageLocatingFace2.name",
      value: {
        label: (value) => `configurator.unit.cstic.carriageLocatingFace.${value}.name`,
      },
    });

    let designSuffix = "";
    const designValue = cstics[UnitCstics.MaterialName]?.value;
    if (designValue === "RUE") {
      designSuffix = ".RUE";
    } else if (designValue === "KUSE") {
      designSuffix = ".KUSE";
    } else if (designValue === "KUVE") {
      designSuffix = ".KUVE";
    } else if (designValue === "KUVE..-W") {
      designSuffix = ".KUVE-W";
    }

    const railCstics = model.kmats[KmatType.SubRail].map((kmat) => kmat.cstics);

    railCstics.forEach((cstics) => {
      const namingHelper = new CsticNamingHelper(cstics);

      namingHelper.updateNames(RailsCstics.GuidewayLength, {
        label: "configurator.unit.cstic.guidewayLength.name",
      });

      namingHelper.updateNames(RailsCstics.PartSizing, {
        label: "configurator.unit.cstic.partSizing.name",
        value: {
          label: (value) => `configurator.unit.cstic.partSizing.${value}.name`,
        },
      });
      namingHelper.updateNames(RailsCstics.PartAmount, {
        label: "configurator.unit.cstic.partAmount.name",
        value: {
          label: (value) => `configurator.unit.cstic.partAmount.${value}.name`,
        },
      });

      namingHelper.updateNames(RailsCstics.PartOneLength, {
        label: instance.t("page.linear.guidewaySet.pieceLength.title", {
          index: 1,
        }),
      });
      namingHelper.updateNames(RailsCstics.PartTwoLength, {
        label: instance.t("page.linear.guidewaySet.pieceLength.title", {
          index: 2,
        }),
      });

      namingHelper.updateNames(RailsCstics.PartThreeLength, {
        label: instance.t("page.linear.guidewaySet.pieceLength.title", {
          index: 3,
        }),
      });

      namingHelper.updateNames(RailsCstics.PartFourLength, {
        label: instance.t("page.linear.guidewaySet.pieceLength.title", {
          index: 4,
        }),
      });

      namingHelper.updateNames(RailsCstics.PartFiveLength, {
        label: instance.t("page.linear.guidewaySet.pieceLength.title", {
          index: 5,
        }),
      });
    });

    const carriageCstics = model.kmats[KmatType.SubCarriage].map((kmat) => kmat.cstics);
    carriageCstics.forEach((cstics) => {
      const namingHelper = new CsticNamingHelper(cstics);

      namingHelper.updateNames(CarriageCstics.LubricationConnectorOrientation, {
        label: "configurator.unit.cstic.lubricationConnectorStatus.name",
        value: {
          label: (value) => `configurator.unit.cstic.lubricationConnectorStatus.${value}.name`,
        },
      });

      namingHelper.updateNames(CarriageCstics.LubricationConnector, {
        label: "configurator.unit.cstic.lubricationConnector.name",
        value: {
          label: (value) => value,
        },
      });

      namingHelper.updateNames(CarriageCstics.LubricationConnectorPosition, {
        label: "configurator.unit.cstic.lubricationConnectorOrientation.name",
        info: "configurator.unit.cstic.lubricationConnectorOrientation.info",
        value: {
          label: (value) => `configurator.unit.cstic.lubricationConnectorOrientation.${value}.name`,
        },
      });

      namingHelper.updateNames(CarriageCstics.LubricationConnectorAlignment, {
        label: "configurator.unit.cstic.angularOrientation.name",
        info: "configurator.unit.cstic.angularOrientation.info",
        value: {
          label: (value) => `configurator.unit.cstic.angularOrientation.${value.replace("°", "")}.name`,
        },
      });

      namingHelper.updateNames(CarriageCstics.CarriageDesign, {
        label: "configurator.unit.step.configureCarriage.section.carriageType.name",
        description: "configurator.unit.step.configureCarriage.section.carriageType.description",
        info: "configurator.unit.step.configureCarriage.section.carriageType.info",
        value: {
          label: (value) => `configurator.unit.cstic.carriageType.${value}.name`,
        },
      });

      namingHelper.updateNames(CarriageCstics.KitsLeft, {
        label: "configurator.unit.cstic.kit.left.name",
        info: "configurator.unit.cstic.kit.left.info" + designSuffix,
      });

      namingHelper.updateNames(CarriageCstics.KitsCenter, {
        label: "configurator.unit.cstic.kit.center.name",
        info: "configurator.unit.cstic.kit.center.info" + designSuffix,
      });

      namingHelper.updateNames(CarriageCstics.KitsRight, {
        label: "configurator.unit.cstic.kit.right.name",
        // "left" on purpose
        info: "configurator.unit.cstic.kit.left.info" + designSuffix,
      });
    });

    return model;
  },
} as const;

const sendStepCompletedEvent = makeSendStepCompletedEvent(LC_DEFINITION);
